import React from 'react';
import { isNil } from 'lodash';
import { useLocale } from 'locale';
import { getBuildingClassKey, PriceEvent } from 'utils/entities';
import { useUniversalCardAddress } from 'components/cards/listing/UniversalCard/utils';
import {
  UniversalCardPoiUnion,
  BulletinUniversalCardProps,
  CardType,
} from 'components/cards/types';
import { UniversalCardExtraProps } from 'components/cards/new-layout/types';
import FavouritesCardButton from 'components/cards/new-layout/components/FavouritesCardButton';
import CardImageWithTags from 'components/cards/new-layout/list-view-card/CardImageWithTags';
import { RenewalProgramIndicator } from './RenewalProgramIndicator';
import CardBodyTags from 'components/cards/new-layout/components/CardBodyTags';
import CompareCheckbox from 'components/cards/new-layout/components/CompareCheckbox';
import { getInvestorMetrics, getPricePerSqm } from './utils';
import * as Styled from './styled';
import { isPriceDropped } from 'utils/priceEvents';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { useABTests } from 'hooks/useABTests';
import { URPotential } from 'config/abTests';

export type InvestorListingCardProps = UniversalCardPoiUnion & UniversalCardExtraProps & { small?: boolean; };

interface ViewProps {
  id: BulletinUniversalCardProps['id'];
  type: BulletinUniversalCardProps['type'];
  address: string;
  imageWithTags: React.ReactElement;
  bodyTags?: React.ReactElement;
  compareCheckbox?: React.ReactElement;
  urbanRenewalStatus?: number;
  propertyType?: string;
  rooms?: number;
  floor?: number;
  areaSqm?: number;
  pricePerSqm?: number;
  gainPercentageAfterRenewal: string;
  yieldValue: string;
  price: number;
  currentRentPrice?: number;
  eventsHistory?: PriceEvent[];
}

const InvestorListingCardView: React.FC<ViewProps> = (props) => {
  const {
    id,
    type,
    address,
    imageWithTags,
    bodyTags,
    compareCheckbox,
    urbanRenewalStatus,
    propertyType,
    rooms,
    floor,
    areaSqm,
    pricePerSqm,
    gainPercentageAfterRenewal,
    yieldValue,
    price,
    currentRentPrice,
    eventsHistory,
  } = props;

  const { t, formatArea, formatMoney } = useLocale();
  const isDevice = useScreenBreakpoint() < 3;
  const hasPriceDropped = isPriceDropped(eventsHistory);

  let prevPrice;
  if (hasPriceDropped) {
    prevPrice = eventsHistory[eventsHistory.length - 2].price;
  }

  const attributeItems: string[] = [
    propertyType || null,
    rooms != null ? t('investorListingCard.attributes.rooms', { count: rooms }) : null,
    floor != null ? t('investorListingCard.attributes.floor', { floor }) : null,
    areaSqm != null ? formatArea(areaSqm) : null,
    pricePerSqm != null
      ? t('investorListingCard.attributes.pricePerSqm', { value: formatMoney(pricePerSqm) })
      : null,
    currentRentPrice
      ? t('investorListingCard.attributes.currentRentPrice', { value: formatMoney(currentRentPrice) })
      : null,
  ].filter(Boolean) as string[];

  const priceDrop = hasPriceDropped ? (
    <Styled.PriceDropped>{formatMoney(prevPrice)}</Styled.PriceDropped>
  ) : null;

  const priceContent = (
    <>
      {isDevice ? priceDrop : null}
      <Styled.MetricValue bigText bigLine={isDevice}>{formatMoney(price)}</Styled.MetricValue>
    </>
  );

  return (
    <Styled.CardWrapper data-auto="investor-listing-card">
      <Styled.FavoriteWrapper>
        <FavouritesCardButton id={id} type={type} size="medium" />
      </Styled.FavoriteWrapper>
      {compareCheckbox ? <Styled.CompareWrapper>{compareCheckbox}</Styled.CompareWrapper> : null}

      <Styled.CardBody>
        <Styled.ImageSection>
          {imageWithTags}
        </Styled.ImageSection>

        <Styled.ContentSection>
          <div>
            <Styled.CardTitle>{address}</Styled.CardTitle>
            {!isNil(urbanRenewalStatus) && urbanRenewalStatus !== -1 ? (
              <Styled.IndicatorRow>
                <RenewalProgramIndicator status={urbanRenewalStatus} noMargin />
                {bodyTags}
              </Styled.IndicatorRow>
            ) : null}
          </div>

          {attributeItems.length > 0 && (
            <Styled.AttributesRow>
              {attributeItems.map((item, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Styled.AttributeSeparator />}
                  <Styled.AttributeText>{item}</Styled.AttributeText>
                </React.Fragment>
              ))}
            </Styled.AttributesRow>
          )}

          <Styled.MetricsGrid>
            <Styled.MetricBox>
              <Styled.MetricLabel>
                {t('investorListingCard.metrics.estimatedRentalYield')}
              </Styled.MetricLabel>
              <Styled.MetricValue>{yieldValue}</Styled.MetricValue>
            </Styled.MetricBox>
            <Styled.MetricBox>
              <Styled.MetricLabel>
                {t('investorListingCard.metrics.gainPercentageAfterRenewal')}
              </Styled.MetricLabel>
              <Styled.MetricValue direction="ltr">{gainPercentageAfterRenewal}</Styled.MetricValue>
            </Styled.MetricBox>
            {/* price spans full width on mobile */}
            <Styled.MetricBox fullWidthOnMobile>
              <Styled.MetricLabel>
                {isDevice ? null : t('investorListingCard.metrics.price')}
                {isDevice ? null : priceDrop}
              </Styled.MetricLabel>
              <ShowAt to={2}>
                <Styled.PriceWrapper>{priceContent}</Styled.PriceWrapper>
              </ShowAt>
              <ShowAt from={3}>{priceContent}</ShowAt>
            </Styled.MetricBox>
          </Styled.MetricsGrid>
        </Styled.ContentSection>
      </Styled.CardBody>
    </Styled.CardWrapper>
  );
};

export const URPotentialCard: React.FC<InvestorListingCardProps> = (props) => {
  const { t } = useLocale();
  const { urPotentialMode } = useABTests();

  if (props.cardType !== CardType.Bulletin) {
    return null;
  }

  const {
    id,
    type,
    thumbnails,
    price,
    bedsCount,
    floor,
    area,
    size,
    buildingClass,
    streetName,
    streetNumber,
    neighbourhood,
    unitNumber,
    city,
    resolutionPreferences,
    isSearchInWholeCountry,
    investorsData,
    urbanRenewalStatus,
    currentRentPrice,
    gainAfterRenewal,
    eventsHistory,
    dealType,
    date,
    lastUpdatedDate,
    logoPath,
    officeId,
    showSchoolInAreaLabel,
    isInvestors,
  } = props as BulletinUniversalCardProps & UniversalCardExtraProps & { small?: boolean };

  const address = useUniversalCardAddress(
    streetName,
    streetNumber,
    neighbourhood,
    unitNumber,
    city,
    resolutionPreferences,
    isSearchInWholeCountry
  );

  const propertyType = buildingClass
    ? t('unitPage.buildingClass', {
      buildingClass: getBuildingClassKey(buildingClass),
      isCommercial: false,
    })
    : undefined;

  const effectiveArea = area || size;
  const pricePerSqm = getPricePerSqm(price, effectiveArea);

  const noDataText = t('investorListingCard.renewalStatus.noData');
  const { yieldValue, gainPercentageAfterRenewal } = getInvestorMetrics(investorsData, gainAfterRenewal, noDataText);

  const isURPotential = !isNil(urbanRenewalStatus) && urbanRenewalStatus !== -1 && urPotentialMode === URPotential.modeB;

  const imageWithTags = (
    <CardImageWithTags
      cardType={CardType.Bulletin}
      small={true}
      logoPath={logoPath}
      thumbnails={thumbnails}
      logoSourceId={officeId || null}
      eventsHistory={eventsHistory || []}
      hasDiscount={false}
      isPromoted={false}
      id={id}
      date={date}
      lastUpdatedDate={lastUpdatedDate}
      dealType={dealType}
      showSchoolInAreaLabel={Boolean(showSchoolInAreaLabel)}
      isURPotential={isURPotential}
      urbanRenewalStatus={isURPotential ? urbanRenewalStatus : undefined}
      hideTags={isInvestors}
    />
  );

  const bodyTags = isInvestors ? (
    <CardBodyTags
      id={id}
      date={date}
      lastUpdatedDate={lastUpdatedDate}
      dealType={dealType}
      eventsHistory={eventsHistory}
      inSearchArea={Boolean(showSchoolInAreaLabel)}
      isURPotential={isURPotential}
      urbanRenewalStatus={urbanRenewalStatus}
    />
  ) : null;

  return (
    <InvestorListingCardView
      id={id}
      type={type}
      address={address}
      imageWithTags={imageWithTags}
      bodyTags={bodyTags}
      compareCheckbox={isInvestors ? <CompareCheckbox id={id} /> : null}
      urbanRenewalStatus={urbanRenewalStatus}
      propertyType={propertyType}
      rooms={bedsCount}
      floor={floor}
      areaSqm={effectiveArea}
      pricePerSqm={pricePerSqm}
      gainPercentageAfterRenewal={gainPercentageAfterRenewal}
      yieldValue={yieldValue}
      price={price}
      currentRentPrice={currentRentPrice}
      eventsHistory={eventsHistory}
    />
  );
};
